import Quill from "quill";
import React, { useCallback, useEffect, useRef } from "react"; // React をインポート
import axios from "axios";
import Cookies from "js-cookie";
import { useAlert } from "../../../../context/AlertContext";
import { useCreditContext } from "../../../../context/CreditContext";
import { TFunction } from "i18next";

interface GreyedTextRange {
  index: number;
  length: number;
}

interface KreaVerseProps {
  quillRef: React.RefObject<Quill | null>;
  fileUuid: string | undefined;
  kreaVerseStatus: "generating" | "waiting" | "ready";
  setKreaVerseStatus: (status: "generating" | "waiting" | "ready") => void;
  t: TFunction<"translation", undefined>;
}

const KreaVerseComponent = ({ quillRef, fileUuid, kreaVerseStatus, setKreaVerseStatus, t }: KreaVerseProps) => {
  // コンポーネント名を変更
  const { setAlert: setGlobalAlert } = useAlert();
  const { setCreditTrigger } = useCreditContext();
  const greyedTextRangeRef = useRef<GreyedTextRange | null>(null);

  const kreaVerse = useCallback(async () => {
    if (kreaVerseStatus !== "ready" || !fileUuid) return;
    const currentQuill = quillRef.current;
    if (!currentQuill) {
      console.error("Quill instance is not available for KreaVerse");
      return;
    }

    if (currentQuill.getText().trim().length < 10) {
      setGlobalAlert("error", t("textEditor.kreaVerse.error"));
      return;
    }

    setKreaVerseStatus("generating");

    if (greyedTextRangeRef.current) {
      currentQuill.deleteText(greyedTextRangeRef.current.index, greyedTextRangeRef.current.length, "user");
      greyedTextRangeRef.current = null;
    }

    let range = currentQuill.getSelection(true);
    if (!range) {
      const currentPosition = currentQuill.getSelection()?.index;
      if (typeof currentPosition !== "number") {
        console.error("Unable to determine cursor position");
        setKreaVerseStatus("ready");
        return;
      }
      range = { index: currentPosition, length: 0 };
    }

    const DESIRED_LENGTH = 500;
    const INITIAL_FETCH = 1000;

    const contentsBefore = currentQuill.getContents(
      Math.max(0, range.index - INITIAL_FETCH),
      Math.min(INITIAL_FETCH, range.index)
    );
    const contentsAfter = currentQuill.getContents(range.index, INITIAL_FETCH);

    const tempQuill = new Quill(document.createElement("div"));
    tempQuill.setContents(contentsBefore);
    let htmlBefore = tempQuill.root.innerHTML;
    tempQuill.setContents(contentsAfter);
    let htmlAfter = tempQuill.root.innerHTML;

    function processHTML(html: string, desiredLength: number, isBefore = false) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const linkCards = doc.querySelectorAll("div.link-card");
      linkCards.forEach((card) => card.remove());
      let text = doc.body.innerHTML
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>|<\/div>|<\/li>/gi, "\n")
        .replace(/<[^>]+>/g, "");
      if (text.length > desiredLength) {
        if (isBefore) {
          text = text.slice(-desiredLength);
          const firstSpace = text.indexOf(" ");
          if (firstSpace > 0) text = text.slice(firstSpace + 1);
        } else {
          text = text.slice(0, desiredLength);
          const lastSpace = text.lastIndexOf(" ");
          if (lastSpace > 0) text = text.slice(0, lastSpace);
        }
      }
      return text;
    }

    htmlBefore = processHTML(htmlBefore, DESIRED_LENGTH, true);
    htmlAfter = processHTML(htmlAfter, DESIRED_LENGTH, false);

    const csrftoken = Cookies.get("csrftoken");
    const headers = { "X-CSRFToken": csrftoken! };
    const data = {
      textBeforeCursor: htmlBefore,
      textAfterCursor: htmlAfter,
      textfile_uuid: fileUuid === undefined ? "new" : fileUuid, // undefinedの場合は "new" を送信
    };

    function onKeyDown(e: KeyboardEvent) {
      if (!greyedTextRangeRef.current || !currentQuill) return;
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        if (e.key === "Enter" || e.key === "Tab") {
          if (currentQuill.getText(greyedTextRangeRef.current.index, 1) === "\n") {
            currentQuill.deleteText(greyedTextRangeRef.current.index, 1, "user");
          }
        }
        currentQuill.formatText(
          greyedTextRangeRef.current.index,
          greyedTextRangeRef.current.length,
          "color",
          "inherit",
          "user"
        );
        currentQuill.setSelection(greyedTextRangeRef.current.index + greyedTextRangeRef.current.length, 0, "user");
        cleanup();
        setKreaVerseStatus("ready");
      } else {
        currentQuill.deleteText(greyedTextRangeRef.current.index, greyedTextRangeRef.current.length, "user");
        cleanup();
        setKreaVerseStatus("ready");
      }
    }

    function onSelectionChange(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rangeValue: any | null
    ) {
      if (!greyedTextRangeRef.current || !currentQuill) return;
      if (rangeValue === null) {
        currentQuill.deleteText(greyedTextRangeRef.current.index, greyedTextRangeRef.current.length, "user");
        cleanup();
        setKreaVerseStatus("ready");
      }
    }

    function onWindowBlur() {
      if (!greyedTextRangeRef.current || !currentQuill) return;
      currentQuill.formatText(
        greyedTextRangeRef.current.index,
        greyedTextRangeRef.current.length,
        "color",
        "inherit",
        "user"
      );
      currentQuill.setSelection(greyedTextRangeRef.current.index + greyedTextRangeRef.current.length, 0, "user");
      cleanup();
      setKreaVerseStatus("ready");
    }

    function onWindowFocus() {
      if (kreaVerseStatus === "waiting") {
        if (!greyedTextRangeRef.current || !currentQuill) return;
        currentQuill.formatText(
          greyedTextRangeRef.current.index,
          greyedTextRangeRef.current.length,
          "color",
          "inherit",
          "user"
        );
        currentQuill.setSelection(greyedTextRangeRef.current.index + greyedTextRangeRef.current.length, 0, "user");
        cleanup();
      }
    }

    function cleanup() {
      if (!currentQuill) return;
      currentQuill.root.removeEventListener("keydown", onKeyDown as EventListener);
      currentQuill.off("selection-change", onSelectionChange);
      window.removeEventListener("blur", onWindowBlur);
      window.removeEventListener("focus", onWindowFocus);
      greyedTextRangeRef.current = null;
    }

    try {
      const res = await axios.post(`/api/v1/text-file/assistant/krea-verse`, data, { headers }); // URLを変更
      if (res.data && res.data.message) {
        currentQuill.history.cutoff();
        const htmlContent = `<span style="color: grey;">${res.data.message}</span>`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const delta = currentQuill.clipboard.convert({ html: htmlContent } as any);
        currentQuill.updateContents(
          [
            { retain: range.index },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { insert: (delta.ops as any)[0].insert, attributes: (delta.ops as any)[0].attributes },
          ],
          "user"
        );
        const insertedLength = delta.length() - 1;
        greyedTextRangeRef.current = { index: range.index, length: insertedLength };
        currentQuill.history.cutoff();
        setKreaVerseStatus("waiting");
        setCreditTrigger(true);
        currentQuill.root.addEventListener("keydown", onKeyDown as EventListener);
        currentQuill.on("selection-change", onSelectionChange);
        window.addEventListener("blur", onWindowBlur);
        window.addEventListener("focus", onWindowFocus);
      }
    } catch (err) {
      setGlobalAlert("error", "An error occurred during the KreaVerse operation");
      console.error(err);
      setKreaVerseStatus("ready");
    }
  }, [fileUuid, kreaVerseStatus, quillRef, setGlobalAlert, setKreaVerseStatus, t, setCreditTrigger]);

  // KreaVerseのトリガーを外部から呼び出せるようにuseEffectでラップ
  useEffect(() => {
    const handleKreaVerseTrigger = () => {
      kreaVerse();
    };
    document.addEventListener("quillKreaVerseTrigger", handleKreaVerseTrigger);
    return () => {
      document.removeEventListener("quillKreaVerseTrigger", handleKreaVerseTrigger);
    };
  }, [kreaVerse]);

  return null; // このコンポーネントはUIを持たない
};
export default React.memo(KreaVerseComponent); // React.memoでラップ
